/**
 * Amplitude adapter.
 * Browser SDK 를 CDN 에서 로드 + init. SDK 빌트인으로 session_start/session_end 와
 * `[Amplitude] Page Viewed` 를 자동 수집한다 (defaultTracking.sessions / pageViews).
 * 그 밖의 커스텀 이벤트는 GBTrack 파이프라인을 통해 발사한다 —
 * 파이프라인에는 page_view 이벤트가 없으므로 페이지 조회의 소스는 SDK 자동 수집뿐이다.
 *
 * FP-1152 작업 4: analytics 동의 전에는 **SDK 스크립트를 아예 로드하지 않는다.**
 *   Browser SDK 2 는 init 즉시 쿠키를 만들고, `deferInitialization` 은 이 버전에 존재하지 않는다.
 *   `optOut: true` 로 init 해도 쿠키는 생기므로 init 호출 자체를 늦추는 것이 유일한 방법이다.
 *   (벤더 문서 확인 — 명세서 작업 4)
 */
(function () {
  'use strict';
  if (!window.GBTrack) { console.warn('[GBTrack:amplitude] core not loaded'); return; }

  var enabled = true;
  var ready = false;
  var sdkRequested = false;
  var lastUserPropsJSON = null;
  // Shopify-측 Amplitude 통합(Shopify Amplitude 앱 / Customer Events Pixel)이 같은 도메인에서
  // window.amplitude 를 점유하면 init/track 이 broken 상태로 빠져 events 가 api.amplitude.com 으로
  // 전혀 송출되지 않는다 (FP-657). 우리 어댑터는 createInstance() 로 격리된 instance 를 잡아 두고
  // 전역 window.amplitude 는 SDK 로드 이전 상태로 되돌린다 (isolateGlobal/restoreGlobal 참고).
  /** @type {Amplitude | null} */ var instance = null;
  // Identify 는 네임스페이스에서 "값"으로 캡쳐한다. sdkNS 참조를 들고 있으면 ScriptTag 스니펫이
  // 뒤늦게 같은 객체를 스텁으로 덮었을 때 new sdkNS.Identify() 가 스텁을 만들어 user_properties 가
  // 조용히 유실된다 (FP-1164 ②).
  /** @type {Function | null} */ var IdentifyCtor = null;

  var USER_PROP_KEYS = ['locale', 'currency', 'customer_id', 'customer_logged_in', 'device_id'];

  function splitContext(context) {
    var user = {};
    var evt = {};
    Object.keys(context).forEach(function (k) {
      if (USER_PROP_KEYS.indexOf(k) !== -1) user[k] = context[k];
      else evt[k] = context[k];
    });
    // A/B 테스트 참여 그룹 `abt: 'id@ver:KEY;…'` → 유저 속성 `abt_<id>: KEY` 도 남긴다.
    // SDK 자동 수집 이벤트([Amplitude] Page Viewed 등)까지 그룹으로 나눠 볼 수 있다 (docs/AB-TEST.md)
    if (context.abt) {
      String(context.abt).split(';').forEach(function (part) {
        var m = /^(.+)@\d+:(.+)$/.exec(part);
        if (m) user['abt_' + m[1]] = m[2];
      });
    }
    return { user: user, evt: evt };
  }

  function analyticsAllowed() {
    var c = window.GBConsent;
    return !!(c && c.isAllowed('analytics'));
  }

  function marketingAllowed() {
    var c = window.GBConsent;
    return !!(c && c.isAllowed('marketing'));
  }

  // 철회 시 Amplitude 가 남긴 식별자 전부 제거 (localStorage / sessionStorage / 쿠키).
  // AMP_* : SDK 2 기본 스토리지, EXP_* : Experiment/미전송 이벤트 큐(EXP_unsent_$default_instance).
  var AMP_KEY_RE = /^(AMP_|EXP_|amp_)/;

  function purgeStore(store) {
    try {
      var doomed = [];
      for (var i = 0; i < store.length; i++) {
        var k = store.key(i);
        if (k && AMP_KEY_RE.test(k)) doomed.push(k);
      }
      doomed.forEach(function (k) { store.removeItem(k); });
    } catch (e) {
      // 스토리지 차단 → 삭제 실패. 식별자가 남을 수 있으므로 기록은 남긴다.
      window.GBTrack.logError('amplitude:purgeStore', e);
    }
  }

  function purgeAmplitudeStorage() {
    purgeStore(window.localStorage);
    purgeStore(window.sessionStorage);
    try {
      var baseDomain = location.hostname.replace(/^www\./, '');
      document.cookie.split(';').forEach(function (c) {
        var name = c.split('=')[0].trim();
        if (!name || !AMP_KEY_RE.test(name)) return;
        document.cookie = name + '=; Max-Age=0; path=/';
        document.cookie = name + '=; Max-Age=0; path=/; domain=.' + baseDomain;
      });
    } catch (e) {
      // 쿠키 접근 차단 → 삭제 실패. 식별자가 남을 수 있으므로 기록은 남긴다.
      window.GBTrack.logError('amplitude:purgeCookies', e);
    }
  }

  // gb_did 를 SDK init 옵션의 deviceId 로 사전 주입 — race 회피.
  // setDeviceId() 를 init 후 호출하면 SDK 가 이미 auto-generated device 로 session_start 1회 fire 후
  // device 전환을 새 session 으로 카운트해 session_id cardinality 가 부풀어진다.
  // ⚠️ 반드시 SDK onload 시점에 읽는다. 동의 직후 tracking-bootstrap 인라인이 gb_did 를 확정하는데,
  //    로드 요청 시점에 읽으면 그 확정보다 앞설 수 있다.
  function resolveDeviceId() {
    try {
      if (window.GBTrack && typeof window.GBTrack.getContext === 'function') {
        var ctx = window.GBTrack.getContext();
        if (ctx && ctx.device_id) return ctx.device_id;
      }
      return localStorage.getItem('gb_did');
    } catch (e) { return null; }
  }

  // ready 전환은 항상 이 함수를 거친다 (onReady 대기자에게 즉시 통지).
  var readyCbs = [];
  function markReady() {
    ready = true;
    var q = readyCbs;
    readyCbs = [];
    q.forEach(function (cb) {
      try { cb(); } catch (e) { console.warn('[GBTrack:amplitude] onReady 콜백 실패', e); }
    });
  }

  // ── window.amplitude 전역 격리 (FP-1164 ②) ─────────────────────────────────
  // Amplitude 의 두 SDK 는 전역 하나를 공유하는 스니펫 규약(`_q` 큐 + `invoked` 플래그)으로 협조한다.
  // 우리가 로드하는 analytics-browser UMD 는 실행 끝에
  //     window.amplitude = Object.assign(window.amplitude || {}, exports, { createInstance })
  // 로 전역을 잡는데, `_q` 도 `invoked` 도 만들지 않는다. 이 상태에서 Shopify Amplitude 앱의
  // ScriptTag 스니펫이 뒤에 실행되면
  //     var d = c.amplitude || { _q: [], _iq: {} }
  // 가 우리 객체를 그대로 채택하고, `d.invoked` 가 falsy 라 스텁 설치 분기로 들어간다.
  // 스텁 본문이 `this._q.push(...)` 인데 `_q` 가 없어 TypeError 로 죽고(=라이브의 'push' 에러),
  // 이어서 ScriptTag 가 로드한 analytics-browser 가 invoked=true 를 보고 `_q` 를 drain 하려다
  // undefined.length 로 한 번 더 죽는다(='length' 에러). 콘솔 에러 2건은 같은 뿌리다.
  //
  // 대응 두 겹:
  //   isolateGlobal  — 스크립트 요청 전에 스니펫 규약 필드를 미리 심는다. onload 보다 스니펫이
  //                    먼저 끼어드는 드문 순서에서도 `_q` 가 있어 크래시하지 않는다.
  //   restoreGlobal  — exports 를 잡은 직후 전역을 로드 이전 상태로 되돌린다. 이후 실행되는
  //                    ScriptTag 스니펫은 우리 흔적을 보지 못하고 자기 스텁을 새로 만든다.
  //                    두 SDK 가 서로의 네임스페이스를 덮지 않는 상태가 된다.
  var globalExisted = false;
  var globalBefore;
  var plantedGlobal = null;

  function isolateGlobal() {
    globalExisted = Object.prototype.hasOwnProperty.call(window, 'amplitude');
    globalBefore = window.amplitude;
    if (globalExisted) return;
    plantedGlobal = { _q: [], _iq: {} };
    window.amplitude = plantedGlobal;
  }

  function restoreGlobal() {
    try {
      // 전역이 이미 있었다면(=ScriptTag 가 먼저 실행됨) UMD 가 그 객체에 in-place 병합했으므로
      // 되돌릴 것이 없다 — 건드리지 않는다.
      if (globalExisted) return;
      // 우리가 심은 객체가 그대로일 때만 치운다. 그 사이 스니펫이 채택해 갔다면(invoked)
      // 지금 지우는 순간 ScriptTag 의 큐가 통째로 사라져 init 이 영영 안 된다 — 그대로 둔다.
      if (window.amplitude !== plantedGlobal || window.amplitude.invoked) return;
      delete window.amplitude;
    } catch (e) { /* noop */ }
  }

  function loadSdk(apiKey) {
    if (sdkRequested || !enabled) return;
    sdkRequested = true;

    var s = document.createElement('script');
    s.async = true;
    // analytics-browser 2.11.1 → 2.42.4 (2026-05) 업그레이드.
    // 누적 픽스: identify call race condition(2.31.4), Safari cookie race(2.36.3),
    // re-entrant cookies.isEnabled(2.33.3), optOut session_start defer(2.34.1) 등.
    // 정정(FP-1164 ②): 예전 주석은 콘솔의 "undefined.length" TypeError 를 Shopify-측 SDK 의 별건으로
    // 적어 두었으나, 라이브 계측 결과 이 에러는 ScriptTag 스니펫이 우리 전역을 채택해 `_q` 가 없는 채
    // drain 에 들어가서 나는 것이었다. 원인은 아래 isolateGlobal/restoreGlobal 이 다루는 전역 충돌 하나다.
    s.src = 'https://cdn.amplitude.com/libs/analytics-browser-2.42.4-min.js.gz';
    isolateGlobal();
    s.onload = function () {
      try {
        var ns = window.amplitude;
        if (!ns || typeof ns.createInstance !== 'function') {
          console.warn('[GBTrack:amplitude] SDK 전역 누락 — createInstance 미존재');
          enabled = false; restoreGlobal(); return;
        }
        // 로드 도중 동의가 철회됐다면 init 하지 않는다 (쿠키 생성 방지).
        if (!analyticsAllowed()) {
          console.warn('[GBTrack:amplitude] SDK 로드 완료 전 동의 철회 → init 생략');
          restoreGlobal(); return;
        }
        // SDK namespace 에서 필요한 것(격리 instance · Identify 생성자)을 즉시 "값"으로 캡쳐한 뒤
        // 전역을 원상복구한다 — 이후 window.amplitude 가 어떻게 되든 우리 송출은 영향 없음.
        instance = ns.createInstance();
        IdentifyCtor = ns.Identify;
        restoreGlobal();

        // onload 보다 ScriptTag 스니펫이 먼저 끼어들어 네임스페이스를 스텁으로 덮은 경우 감지.
        // 실 SDK client 는 `_q` 를 노출하지 않는다 — 스텁을 잡았다면 송출이 통째로 유실되므로
        // 조용히 도는 것보다 끄고 경고하는 편이 낫다.
        if (!instance || Array.isArray(instance._q)) {
          console.warn('[GBTrack:amplitude] ScriptTag 스니펫이 네임스페이스 선점 — 스텁 감지, 어댑터 비활성');
          enabled = false; instance = null; return;
        }

        // sessions: SDK 빌트인 session_start/session_end 자동 발사 (Bug B fix).
        // pageViews: FP-1164 로 true 전환. GBTrack 파이프라인에 page_view 이벤트가 없으므로
        //   `[Amplitude] Page Viewed` 의 유일한 소스는 SDK 자동 수집이다.
        //   ⚠️ 배포는 Amplitude ScriptTag 자동 수집 off(FP-1167 A-1)와 붙여서 해야 한다 —
        //   먼저 나가면 이중 집계, 늦게 나가면 계측 공백. 겹치는 구간은 device_id 로 구분 가능
        //   (우리 인스턴스 = gb_did, ScriptTag = 자체 발급).
        // fetchRemoteConfig:false — 대시보드 Autocapture 토글은 원격설정으로 내려오고, 명시적 값은
        //   코드 설정을 덮는다(Amplitude 지원 #406359). API key 를 ScriptTag 와 공유하므로 이 옵션이
        //   없으면 FP-1167 의 대시보드 Page Views Off 가 우리 인스턴스까지 꺼서 Page Viewed 가 전면
        //   소실된다. 수신을 끊어 두면 대시보드 Off 는 ScriptTag 에만 걸리고 여기 코드 설정이 유지된다.
        //   ⚠️ 이 옵션을 되돌리면 대시보드 값이 아래 defaultTracking 을 이긴다.
        // formInteractions / fileDownloads: FP-1167 A-1 에서 대시보드로 끈 항목. 여기서도 끈 상태를 유지한다.
        // ⚠️ defaultTracking 은 SDK 2.x 에서 deprecated 이고 autocapture 가 현행 옵션이지만,
        //   부분 객체를 넘길 때의 병합 규칙 차이로 elementInteractions 가 되살아날 위험이 있어
        //   (FP-1167 A-1 에서 끈 최대 볼륨 이벤트) 이번에는 키 하나만 바꾼다. 마이그레이션은 별건.
        // attribution: FP-1327 — **적지 않으면 켜진다.** SDK 의 판정은 `옵션[키] !== false` 여서
        //   부분 객체의 미지정 키는 기본 활성이다(2.42.4 검증). 이 플러그인은 fbclid · gclid ·
        //   ttclid · msclkid 를 걷어 AMP_MKTG_<apiKey> 쿠키에 담는다 — 우리 카테고리 표에서
        //   클릭 ID 는 marketing 이므로(docs/CONSENT.md §4) analytics 동의만으로 켜면 안 된다.
        //   ⚠️ init 시점 값으로 고정된다. 나중에 마케팅에 동의해도 플러그인을 뒤늦게 붙이지
        //   않는다(다음 페이지 로드부터 반영). 철회 시 AMP_MKTG_* 는 AMP_KEY_RE 정리 대상이다.
        // flushQueueSize:1 + transport:'beacon' 은 <a> 네비게이션 직후 in-flight fetch abort 로 이벤트 손실 방지.
        // deviceId 를 init 시점에 주입 → SDK 가 첫 session 부터 gb_did 사용 (session_id 부풀림 race fix).
        var initOpts = {
          defaultTracking: {
            sessions: true,
            pageViews: true,
            formInteractions: false,
            fileDownloads: false,
            attribution: marketingAllowed(),
          },
          fetchRemoteConfig: false,
          flushQueueSize: 1,
          flushIntervalMillis: 0,
          transport: 'beacon',
        };
        var did = resolveDeviceId();
        if (did) initOpts.deviceId = did;
        instance.init(apiKey, undefined, initOpts);
        markReady();
      } catch (err) {
        console.warn('[GBTrack:amplitude] init 실패', err); enabled = false; restoreGlobal();
      }
    };
    s.onerror = function () {
      console.warn('[GBTrack:amplitude] SDK 로드 실패 — 세션 내 비활성');
      enabled = false;
      // 로드 실패 시 미리 심어 둔 스텁이 남으면 ScriptTag 스니펫이 그걸 채택해 버린다.
      restoreGlobal();
    };
    document.head.appendChild(s);
  }

  var adapter = {
    name: 'amplitude',
    init: function (adapterCfg) {
      if (!adapterCfg || !adapterCfg.key || !String(adapterCfg.key).trim()) {
        console.warn('[GBTrack:amplitude] api_key 비어 있음 → 어댑터 비활성');
        enabled = false; return;
      }
      var apiKey = String(adapterCfg.key).trim();

      var consent = window.GBConsent;
      if (!consent || typeof consent.whenAllowed !== 'function') {
        console.warn('[GBTrack:amplitude] GBConsent 미로드 → SDK 로드 보류(fail-closed)');
        enabled = false; return;
      }
      // analytics 동의가 실제로 있을 때 딱 1회 로드. 거부/미응답이면 스크립트 요청조차 나가지 않는다.
      consent.whenAllowed('analytics', function () { loadSdk(apiKey); });
      // 철회 후 재동의(whenAllowed 는 이미 소진)에도 로드가 필요하다.
      adapter._pendingApiKey = apiKey;
    },
    isReady: function () { return enabled && ready && !!instance && analyticsAllowed(); },
    isAllowed: function () { return enabled && analyticsAllowed(); },
    // 코어 기본 폴링(60초 타임아웃)은 "동의 대기"를 로드 실패로 오판하고, 거부한 이용자에게는
    // 세션 내내 도는 인터벌이 남는다. init 성공 시점에 직접 통지하는 방식으로 대체한다.
    onReady: function (cb) {
      if (adapter.isReady()) { cb(); return; }
      readyCbs.push(cb);
    },
    transform: function (event, props, context) {
      var split = splitContext(context);
      return {
        event_type: event,
        event_properties: Object.assign({}, split.evt, props),
        user_properties: split.user,
      };
    },
    send: function (payload) {
      // isReady() 가 true 인 동안엔 instance/IdentifyCtor 가 non-null 이지만 TS strictNullChecks 가
      // 함수 경계를 못 넘으므로 명시 가드.
      if (!adapter.isReady() || !instance || !IdentifyCtor) return;
      // device_id 는 init() 옵션으로 사전 주입되어 setDeviceId() 호출 불필요.

      if (payload.user_properties && Object.keys(payload.user_properties).length) {
        var uJson = JSON.stringify(payload.user_properties);
        if (uJson !== lastUserPropsJSON) {
          lastUserPropsJSON = uJson;
          try {
            // Identify 생성자는 로드 시점에 캡쳐해 둔 값을 쓴다 (전역 재조회 금지).
            var identify = new IdentifyCtor();
            Object.keys(payload.user_properties).forEach(function (k) {
              identify.set(k, payload.user_properties[k]);
            });
            instance.identify(identify);
          } catch (e) {
            console.warn('[GBTrack:amplitude] Identify 실패, user_properties 미전송', e);
          }
        }
      }
      instance.track(payload.event_type, payload.event_properties);
    },
    setUserId: function (id) {
      if (!adapter.isReady() || !instance) return;
      try { instance.setUserId(id ? String(id) : undefined); }
      catch (e) { console.warn('[GBTrack:amplitude] setUserId 실패', e); }
    },
    setDeviceId: function (did) {
      if (!adapter.isReady() || !instance) return;
      try { instance.setDeviceId(String(did)); }
      catch (e) { console.warn('[GBTrack:amplitude] setDeviceId 실패', e); }
    },

    // FP-1152 작업 7 · 철회: 전송 중단 + SDK optOut + 세션/식별자 리셋 + 저장된 식별자 전부 삭제.
    onConsentRevoked: function (revoked) {
      if (!revoked.analytics) return;
      ready = false;
      lastUserPropsJSON = null;
      if (instance) {
        try { instance.setOptOut(true); } catch (e) { console.warn('[GBTrack:amplitude] setOptOut 실패', e); }
        try { instance.reset(); } catch (e) { console.warn('[GBTrack:amplitude] reset 실패', e); }
      }
      // reset() 이 남기는 잔여 키까지 훑어 지운다 (AMP_* · EXP_* · 미전송 이벤트 큐).
      purgeAmplitudeStorage();
    },

    // 재동의: 신규 식별자로 초기화. SDK 를 한 번도 안 받았다면 이제 받는다.
    onConsentRestored: function (restored) {
      if (!restored.analytics) return;
      if (!instance) { loadSdk(adapter._pendingApiKey); return; }
      try {
        instance.setOptOut(false);
        instance.reset(); // 새 device_id · 새 session
        var did = resolveDeviceId();
        if (did) instance.setDeviceId(String(did));
        markReady();
      } catch (e) { console.warn('[GBTrack:amplitude] 재동의 초기화 실패', e); }
    },
  };

  window.GBTrack.register(adapter);
})();
